// Stateless calendar: the panel owns selection and coverage requests.
import React from 'react';
import DateFnsUtils from '@date-io/date-fns';
import fiLocale from 'date-fns/locale/fi';
import svLocale from 'date-fns/locale/sv';
import enLocale from 'date-fns/locale/en-GB';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { createTheme, MuiThemeProvider } from '@material-ui/core/styles';

import { getStyle } from '../../utils/utils';
import { fleetLocationStyles as styles } from './fleetLocationStyles';

// The picker speaks Date, the API speaks a calendar day. Built from the LOCAL parts, not
// from `toISOString()`, which would hand back the UTC day and be wrong every evening.
function dayValue(date) {
  const pad = (n) => (n < 10 ? '0' + n : String(n));
  return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
}

function todayValue() {
  return dayValue(new Date());
}

function dayAsDate(day) {
  const parts = String(day).split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => !isFinite(n))) {
    return new Date();
  }
  return new Date(parts[0], parts[1] - 1, parts[2]);
}

// The picker is a white MUI surface dropped onto a black panel. Only the palette is
// overridden: everything else about the calendar is MUI's, including the Finnish week
// starting on Monday, which comes from the date-fns locale rather than from us.
const PICKER_THEME = createTheme({
  palette: {
    type: 'dark',
    primary: { main: '#4FC3F7' },
    // SPELLED OUT, NOT INHERITED. With a transparent surface MUI cannot derive a contrast
    // colour, and its guess was a grey that read as "every day is disabled".
    text: {
      primary: '#FFFFFF',
      secondary: 'rgba(255, 255, 255, 0.85)',
      hint: 'rgba(255, 255, 255, 0.7)',
      disabled: 'rgba(255, 255, 255, 0.35)',
    },
    background: { paper: 'rgba(0, 0, 0, 0.4)', default: 'transparent' },
  },
  // The picker paints its own day numbers from its own rules rather than from the palette,
  // and the panel's backdrop is a photograph -- grey-on-grey in the last week of the month.
  // A day nobody can read is a day nobody can pick.
  overrides: {
    MuiPickersDay: {
      // The digit sits in a Typography that the app paints black through
      // `p.MuiTypography-root`. That selector scores the same as a plain `& *`, so the
      // later sheet won; `&&` repeats the day class and settles it on specificity.
      day: { color: '#FFFFFF', '&& *': { color: 'inherit' } },
      // TODAY, PUT BACK. The picker marks the current day in the primary colour by default,
      // and the white above erased it -- so a calendar of thirty identical digits gave a
      // reader browsing last week nothing to navigate from. Painting it white was the point
      // of that rule; painting it over `current` was the accident. `&&` matches the
      // specificity of the rule above, and `current` is defined after `day` in the picker's
      // own sheet, so the later rule settles it.
      current: { color: '#4FC3F7', fontWeight: 700, '&& *': { color: 'inherit' } },
      dayDisabled: { color: 'rgba(255, 255, 255, 0.35)' },
    },
    MuiPickersCalendarHeader: {
      switchHeader: { color: '#FFFFFF', '&& p': { color: 'inherit' } },
      dayLabel: { color: 'rgba(255, 255, 255, 0.7)' },
      iconButton: { backgroundColor: 'transparent', color: '#FFFFFF' },
    },
    MuiPickersBasePicker: { pickerView: { backgroundColor: 'rgba(0, 0, 0, 0.4)' } },
  },
});

// The selected day, as the instants that bound it HERE, sent as absolute times. Building
// the range from the local Date is what makes "Monday" mean the operator's Monday rather
// than UTC's.
//
// THE END IS THE NEXT LOCAL MIDNIGHT, NOT THE START PLUS 24 HOURS. On the two nights a
// year the clocks move those are different instants, and the difference is invisible: a
// fixed span reaches an hour into Tuesday in spring and stops an hour before midnight in
// autumn, with nothing on screen to say so. The autumn day runs 25 hours and the endpoint
// refuses more than 24, so it is clamped -- one hour of one night lost, rather than a 400
// and no day at all. Lifting that needs the API's own cap raised, which this screen cannot.
const MAX_WINDOW_MS = 24 * 60 * 60 * 1000;

// What to offer when the organisation's own choice has not reached this screen: the LIVE
// route's own bound, `_MAX_HISTORY_AGE` in fleetlocation/api/history_window.py, not twelve calendar
// months. The two differ by a day across a leap day, and that day is one the route refuses --
// the picker's whole job here is never to offer a day the backend will not answer for.
const LIVE_ROUTE_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

// Local wall-clock time, zero-padded. The clamp is about which hour of the operator's own
// evening went missing, so it is shown in the operator's own clock.
function formatClockTime(date) {
  const pad = (n) => (n < 10 ? '0' + n : String(n));
  return pad(date.getHours()) + ':' + pad(date.getMinutes());
}

function windowFor(day) {
  const parts = String(day).split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => !isFinite(n))) {
    return { valid: false, malformed: true };
  }
  const start = new Date(parts[0], parts[1] - 1, parts[2], 0, 0, 0, 0);
  // A date the calendar does not have -- an emptied picker, a 13th month, a 31st of June --
  // arrives as a Date that silently means some other day. Round-tripping is what catches it.
  if (start.getFullYear() !== parts[0]
    || start.getMonth() !== parts[1] - 1
    || start.getDate() !== parts[2]) {
    return { valid: false, malformed: true };
  }
  const rollover = new Date(start.getTime());
  rollover.setDate(rollover.getDate() + 1);
  const cap = new Date(start.getTime() + MAX_WINDOW_MS);
  const clamped = rollover > cap;
  const dayEnd = clamped ? cap : rollover;
  const now = new Date();
  const end = dayEnd > now ? now : dayEnd;
  // THE HOUR THAT WAS LOST, NAMED. A comment in this file is not a disclosure: the screen
  // is where somebody wonders why the evening is missing, so the clamp travels out with
  // the range as the local time the window actually stops at.
  return {
    from: start.toISOString(),
    to: end.toISOString(),
    valid: end > start,
    clampedAt: clamped ? formatClockTime(dayEnd) : null,
  };
}

// Retention's oldest allowed instant: the same clock time `months` back.
//
// A MONTH TOO SHORT TO HOLD THE DAY CLAMPS SHORT. Three months before the 31st of May is a
// date February does not have, and the answer is the 28th, not the 1st: the store's expiry
// job clamps that way (fleetlocation/retention.py), so offering the 1st would offer three
// weeks of days the backend has already deleted -- an empty answer that reads as "the
// vehicle stood still".
function retentionCutoff(now, months) {
  if (!months) {
    return new Date(now.getTime() - LIVE_ROUTE_MAX_AGE_MS);
  }
  const cutoff = new Date(
    now.getFullYear(), now.getMonth() - months, 1,
    now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds()
  );
  const lastDayOfCutoffMonth = new Date(
    cutoff.getFullYear(), cutoff.getMonth() + 1, 0
  ).getDate();
  cutoff.setDate(Math.min(now.getDate(), lastDayOfCutoffMonth));
  return cutoff;
}

// WHOLE DAYS, NOT INSTANTS. The picker hands over midnight of the day tapped, while the
// cutoff carries the current clock time, so comparing the two directly would disable the
// oldest retained day for every hour of the day except the first.
function isBeforeCutoffDay(picked, cutoff) {
  const pickedDay = new Date(picked.getFullYear(), picked.getMonth(), picked.getDate());
  const cutoffDay = new Date(cutoff.getFullYear(), cutoff.getMonth(), cutoff.getDate());
  return pickedDay.getTime() < cutoffDay.getTime();
}

// COVERAGE IS FETCHED PER DISPLAYED MONTH, so the month is the cache key and the query.
function monthValue(date) {
  const month = date.getMonth() + 1;
  return date.getFullYear() + '-' + (month < 10 ? '0' : '') + month;
}

function VehicleHistoryCalendar({ day, coverage, retentionMonths, language, t,
  screenSize, onDayChange, onMonthChange }) {
  const locale = { fi: fiLocale, sv: svLocale, en: enLocale }[
    String(language || 'en').toLowerCase().split('-')[0]
  ] || enLocale;

  function decorateDay(picked, dayInCurrentMonth, dayComponent) {
    // The grid carries the tail of the previous month and the head of the next. Marking
    // those would put a verdict on days this month's view cannot select.
    if (!dayInCurrentMonth) {
      return dayComponent;
    }
    const status = coverage[dayValue(picked)] || 'unknown';
    const mark = '#4FC3F7';
    // Moved is filled, still is an outline, unknown is neither and dimmed. A DAY THE STORE
    // DOES NOT HOLD MUST NOT LOOK LIKE A DAY THE VEHICLE DID NOT MOVE -- the two are
    // different answers, and merging them is the defect being fixed underneath this.
    // ABOVE THE NUMERAL, not below it. Every calendar a fleet manager already uses -- the
    // phone in their pocket included -- puts the day's mark over the date, so a mark under it
    // reads as belonging to the row beneath.
    const dot = {
      position: 'absolute',
      top: 2,
      left: '50%',
      marginLeft: -3,
      width: 6,
      height: 6,
      borderRadius: 3,
      boxSizing: 'border-box',
      background: status === 'moved' ? mark : 'transparent',
      border: '1px solid ' + (status === 'still' ? mark : 'transparent'),
    };
    return React.cloneElement(
      dayComponent,
      {
        'data-coverage': status,
        'aria-label': dayValue(picked) + ': ' + t('historyCoverage.' + status, {
          keySeparator: '.',
        }),
        style: {
          ...dayComponent.props.style,
          position: 'relative',
          opacity: status === 'unknown' ? 0.55 : 1,
        },
      },
      dayComponent.props.children,
      <span key='coverage-dot' style={dot} />
    );
  }

  return (
    <div style={{ flex: '0 0 auto' }}>
      <label style={getStyle(screenSize, styles, 'label')} htmlFor='history-day'>
        {t('historyDayLabel')}
      </label>
      {/* Retention uses the current clock and instruction on every render. */}
      <MuiThemeProvider theme={PICKER_THEME}>
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale}>
          <DatePicker
            id='history-day'
            variant='static'
            disableToolbar
            openTo='date'
            value={dayAsDate(day)}
            maxDate={new Date()}
            shouldDisableDate={(picked) => isBeforeCutoffDay(
              picked, retentionCutoff(new Date(), retentionMonths)
            )}
            onChange={(picked) => onDayChange(dayValue(picked))}
            onMonthChange={(shown) => onMonthChange(monthValue(shown))}
            renderDay={(picked, selected, inMonth, dayComponent) => decorateDay(
              picked, inMonth, dayComponent
            )}
          />
        </MuiPickersUtilsProvider>
      </MuiThemeProvider>
    </div>
  );
}

export { dayValue, todayValue, dayAsDate, monthValue, windowFor, retentionCutoff, PICKER_THEME };
export default VehicleHistoryCalendar;
